import type { MapWidgetData } from '@web/widgets/map/type';

import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { Widgets } from '@common/enums';

import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { CredentialsFacade } from './credentials.facade';

import { CredentialsService } from './credentials.service';

import WidgetsLegacyActions from '@web/store/page-structure/widgets/widgets.actions';
import * as CredentialsActions from './credentials.actions';

@Injectable()
export class CredentialsEffects {
    private readonly actions$ = inject(Actions);
    private readonly credentialsFacade = inject(CredentialsFacade);
    private readonly credentialsService = inject(CredentialsService);
    private readonly widgetsFacade = inject(WidgetsFacade);

    /**
     * Append Google Maps script to the DOM with SendPulse API key (for constructor only)
     *
     * @memberof CredentialsEffects
     */
    public readonly initializeMapsApiOnConstructorLoad$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WidgetsLegacyActions.init),
            filter(({ widgets }) => widgets.some((widget) => widget.name === Widgets.MAP)),
            map(() => CredentialsActions.loadMapsApi())
        );
    });

    public readonly loadApiWithMapWidget$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WidgetsLegacyActions.widgetCreated),
            filter(({ widget }) => widget.name === Widgets.MAP),
            map(() => CredentialsActions.loadMapsApi())
        );
    });

    public readonly loadMapsApi$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CredentialsActions.loadMapsApi),
            mergeMap(() => this.credentialsService.loadMapsAPi()),
            map(() => CredentialsActions.mapsApiLoaded())
        );
    });

    public readonly setApiKey$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CredentialsActions.setMapsApiKey),
            withLatestFrom(this.widgetsFacade.widgets$, (action, widgets) => ({
                apiKey: action.apiKey,
                widgets: widgets.filter(
                    (widget) =>
                        widget.name === Widgets.MAP &&
                        ((widget.data as MapWidgetData).apiKey || '') !== (action.apiKey || '')
                ),
            })),
            switchMap(({ apiKey, widgets }) => {
                return widgets.map((widget) => {
                    return WidgetsLegacyActions.updateWidgetData({
                        update: {
                            id: widget.id,
                            changes: {
                                data: {
                                    apiKey,
                                } as MapWidgetData,
                            },
                        },
                    });
                });
            })
        );
    });
}
